import React, { useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { useAtomValue, useSetAtom } from 'jotai'

import * as lib from '../../lib'
import * as state from '../../states'
import { IAffiliate } from '../../types'
import {
	IPercentRecord,
	AddBoxesWrapper,
	AddRecordBox,
	AddBoxes,
	AddBox,
	DateBox,
	PercentBox,
	RemoveRecordBox,
	RemoveBtn,
} from '../revenueAgency'
import dt from 'dayjs'
import { IconPlus, IconDeleteBin } from '../ui'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'

type IAffiliateEditModalProps = {
	item: IAffiliate | null;
	show: boolean;
	onClose: () => void;
	onSave: () => void;
}

export const AffiliateEditModal = (
	props: IAffiliateEditModalProps
) => {
	const { t } = useTranslation()
	const isThemeDark = useAtomValue(state.themeIsDarkAtom)
	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)
	const tokens = useAtomValue(state.tokensAtom)
	const access = tokens?.access ?? null

	const { item, show, onClose, onSave } = props
	const today = React.useMemo(() => dt.utc(new Date()).startOf('day'), [])

	const nameInit = item?.name ?? ''
	const [name, setName] = useState<string>(nameInit)

	const initPercentRecord: IPercentRecord = {
		date: dt.utc(today).toDate(),
		percent: ''
	}

	const initPercentRecords: IPercentRecord[] = item?.percents?.map(item => ({
		date: dt.utc(item.at).toDate(),
		percent: item.value?.toString()
	})) ?? [{ ...initPercentRecord }]

	const [percentRecords, setPercentRecords] = useState<IPercentRecord[]>(initPercentRecords)

	const [notes, setNotes] = useState<string>(item?.notes ?? '')

	//console.log({ props, item })
	if (!show) {
		return null
	}

	const onUpdate = async () => {
		const uri: string = `affiliate/${item?.id}`
		const method: string = 'PATCH'
		const params: lib.FetchOptions['params'] = {}
		const headers = lib.bearer(access)

		const body: string = JSON.stringify({
			name,
			percents: percentRecords.filter(item => item.percent && item.date).map(item => ({
				at: item.date,
				value: +item.percent
			})),
		})
		const options: lib.FetchOptions = { body, method, params, headers }

		try {
			setFetchIsLoading(state.turnOnLoader)
			await lib.fetching<void>(uri, options)
			onSave()
		} catch (error: unknown) {
			// TODO: pretty notification alert
			console.error(error)
		} finally {
			setFetchIsLoading(state.turnOffLoader)
		}
	}

	const onAddRecord = (event: React.MouseEvent) => {
		event.preventDefault()

		setPercentRecords((prev) => {
			const newArr = [
				{
					...initPercentRecord
				},
				...prev
			]
			//console.log('onAddRecord click!', { prev, newArr })
			return newArr
		})
	}
	const onRemoveRecord = (event: React.MouseEvent, index: number) => {
		event.preventDefault()

		setPercentRecords((prev) => {
			const newArr = prev.filter((item, i) => i !== index)
			//console.log('onRemoveRecord click!', { index, prev, newArr })
			return newArr
		})
	}

	const modalTitle = `${t(ALL_LANG_KEY.affiliate_crud_modal.title_edit)} #${item?.id}`
	const updateButtonTitle = `${t(ALL_LANG_KEY.affiliate_crud_modal.btn_update)}`

	return <>
		<Modal show={show}
		       onHide={onClose}
		       scrollable={true}
		>
			<Modal.Header closeButton>
				<Modal.Title>{modalTitle}</Modal.Title>
			</Modal.Header>

			<Modal.Body className={'scroll'}>
				<Form>
					<Form.Group>
						<Form.Label className="text-dark" htmlFor={'input-name'}>{`${t(ALL_LANG_KEY.affiliate_crud_modal.field_name_label)}`}</Form.Label>
						<Form.Control
							id={'input-name'}
							disabled={true}
							placeholder={`${t(ALL_LANG_KEY.affiliate_crud_modal.field_name_placeholder)}`}
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>
						<br/>

						<AddBoxesWrapper>
							<AddRecordBox>
								<button
									type="button"
									className="btn btn-primary d-flex align-items-center"
									onClick={(e) => onAddRecord(e)}
								>
									<IconPlus/>
									<span className="d-none d-lg-block">{`${t(ALL_LANG_KEY.affiliate_crud_modal.btn_add_record)}`}</span>
								</button>
							</AddRecordBox>

							<AddBoxes>
								{percentRecords.map((item, index) => {
									const isRemoveBtnActive = percentRecords.length > 1

									return (
										<AddBox key={`add-box-${index}`}>
											<DateBox>
												<Form.Label className="text-dark" htmlFor={`input-date-${index}`}>{`${t(ALL_LANG_KEY.affiliate_crud_modal.field_date_label)}`}</Form.Label>
												<Form.Control
													id={`input-date-${index}`}
													placeholder={`${t(ALL_LANG_KEY.affiliate_crud_modal.field_date_placeholder)}`}
													type="date"
													value={item.date ? dt.utc(item.date).format('YYYY-MM-DD') : undefined}
													disabled={false}
													onChange={
														(e) => {
															const value = e.target.value
															const v = dt.utc(value)
															//console.log('onDateChange: ', { index, value })
															// update only for isValid date (not to reset\clear)
															v.isValid() && setPercentRecords((prev) => {
																const newArr = [...prev]
																newArr[index].date = v.startOf('day').toDate()

																return newArr
															})
														}
													}
												/>
											</DateBox>
											<PercentBox>
												<Form.Label className="text-dark" htmlFor={`input-percent-${index}`}>{`${t(ALL_LANG_KEY.affiliate_crud_modal.field_percent_label)}`}</Form.Label>
												<Form.Control
													id={`input-percent-${index}`}
													placeholder="0"
													type="number"
													value={item.percent ?? ''}
													onChange={(e) => {
														const value = e.target.value
														//console.log('onPercentChange: ', { index, value })
														setPercentRecords((prev) => {
															const newArr = [...prev]
															newArr[index].percent = value

															return newArr
														})
													}}
												/>
											</PercentBox>

											<RemoveRecordBox>
												<RemoveBtn data-disabled={!isRemoveBtnActive}
												           onClick={(e) => isRemoveBtnActive && onRemoveRecord(e, index)}
												>
													<IconDeleteBin size={16}/>
												</RemoveBtn>
											</RemoveRecordBox>
										</AddBox>
									)
								})}
							</AddBoxes>
						</AddBoxesWrapper>

						<Form.Label className="text-dark" htmlFor={'input-notes'}>{`${t(ALL_LANG_KEY.affiliate_crud_modal.field_notes_label)}`}</Form.Label>
						<Form.Control
							id={'input-notes'}
							disabled={true}
							placeholder={`${t(ALL_LANG_KEY.affiliate_crud_modal.field_notes_placeholder)}`}
							type="text"
							multiple={true}
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
						/>
					</Form.Group>
				</Form>
			</Modal.Body>

			<Modal.Footer>
				<Button variant="primary" onClick={onUpdate}>{updateButtonTitle}</Button>
				<Button variant="secondary" onClick={onClose}>{`${t(ALL_LANG_KEY.affiliate_crud_modal.btn_close)}`}</Button>
			</Modal.Footer>
		</Modal>
	</>
}
